import { ReactElement, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Device } from '@/api';
import {
    UIAvatar,
    UIIcon,
    UISection,
    UISectionContent,
    UISectionHeader,
    UIText
} from '@/ui';
import { red } from '@/ui/styles';
import { UpdateDeviceModal } from './update-device-modal';
import { useDevicesTranslation } from '../hooks';
import { SectionRow } from './section-row';

export type DeviceGeneralSectionProps = {
    device: Device;
    /** Whether the current user may edit this device. */
    canEdit: boolean;
};

/**
 * "General" section of the device page: shows the device name and the team and
 * member it is assigned to, with an Edit action (when the user may edit) that
 * opens the device update modal.
 */
export function DeviceGeneralSection(
    props: DeviceGeneralSectionProps
): ReactElement {
    const { device, canEdit } = props;
    const { t } = useDevicesTranslation();

    const companyId = device.companyId ?? '';

    const [isEditOpen, setIsEditOpen] = useState(false);

    const teamName = useMemo(() => {
        if (device.team) {
            return `${device.team.name}`;
        }
        return t('shared.devices.no_team');
    }, [device.team, t]);

    const userName = useMemo(() => {
        if (device.user) {
            if (device.user.joined) {
                return `${device.user.firstName} ${device.user.lastName}`;
            }
            return `${device.user.email}`;
        }
        return t('components.device_general_section.no_member');
    }, [device.user, t]);

    return (
        <>
            <UISection>
                <UISectionHeader
                    title={t('components.device_general_section.title')}
                    rightButtons={
                        canEdit
                            ? [
                                  {
                                      text: t('shared.labels.edit'),
                                      variant: 'textDefault',
                                      type: 'button',
                                      onClick: () => setIsEditOpen(true)
                                  }
                              ]
                            : []
                    }
                />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <SectionRow
                            label={t('shared.fields.name.label')}
                            description={t(
                                'components.device_general_section.name_description'
                            )}
                        >
                            <UIText>{device.name}</UIText>
                        </SectionRow>
                        <SectionRow
                            label={t('shared.fields.team.label')}
                            description={t('shared.devices.team_description')}
                        >
                            {device.team ? (
                                <Link
                                    to={`/company/${companyId}/settings/teams/${device.team.teamId}`}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        textDecoration: 'none'
                                    }}
                                >
                                    <UIAvatar
                                        size={40}
                                        img={device.team.image ?? ''}
                                        alt={teamName}
                                    />
                                    <UIText type="link">{teamName}</UIText>
                                </Link>
                            ) : (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8
                                        }}
                                    >
                                        <UIIcon
                                            name="alert-triangle"
                                            size={16}
                                            color={red}
                                        />
                                        <UIText type="error">
                                            {t('shared.devices.no_team')}
                                        </UIText>
                                    </div>
                                    <UIText variant="small" type="default2">
                                        {t(
                                            'components.device_general_section.no_team_default'
                                        )}
                                    </UIText>
                                </div>
                            )}
                        </SectionRow>
                        <SectionRow
                            label={t('shared.fields.member.label')}
                            description={t(
                                'components.device_general_section.member_description'
                            )}
                        >
                            {device.user ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8
                                    }}
                                >
                                    <UIAvatar
                                        size={40}
                                        img={device.user.image ?? ''}
                                        alt={userName}
                                    />
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2
                                        }}
                                    >
                                        <UIText>{userName}</UIText>
                                        {device.user.joined && (
                                            <UIText type="default2">
                                                {device.user.email}
                                            </UIText>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <UIText type="default2">{userName}</UIText>
                            )}
                        </SectionRow>
                    </div>
                </UISectionContent>
            </UISection>
            <UpdateDeviceModal
                device={device}
                isOpen={isEditOpen}
                onClose={() => setIsEditOpen(false)}
            />
        </>
    );
}
